import React from 'react';
import { InfoCard, Link } from '@backstage/core-components';
import { useEntity } from '@backstage/plugin-catalog-react';
import { Box, Typography, Button } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import BarChartIcon from '@material-ui/icons/BarChart';
import { stringifyEntityRef } from '@backstage/catalog-model';
import { useApi, identityApiRef } from '@backstage/core-plugin-api';
import { useAsync } from 'react-use';
import { useAnalyticsAccessConfig } from '../../hooks/useAnalyticsAccessConfig';

const useStyles = makeStyles((theme) => ({
  content: {
    padding: theme.spacing(2),
    textAlign: 'center',
  },
  icon: {
    fontSize: 48,
    color: theme.palette.primary.main,
    marginBottom: theme.spacing(2),
  },
  button: {
    marginTop: theme.spacing(2),
  },
}));

/**
 * Card that displays a link to view analytics for the current user entity
 * Only visible to users in the backstage_operators group
 */
export const EntityAnalyticsCard = () => {
  const classes = useStyles();
  const { entity } = useEntity();
  const identityApi = useApi(identityApiRef);
  
  // Get allowed groups from config
  const allowedGroups = useAnalyticsAccessConfig();
  
  // Get user's group memberships
  const { value: userIdentity, loading } = useAsync(async () => {
    const identity = await identityApi.getBackstageIdentity();
    return identity.ownershipEntityRefs;
  }, [identityApi]);

  const userOwnership = userIdentity || [];

  // If no groups are configured, hide the card
  if (allowedGroups.length === 0) {
    return null;
  }

  // Wait for user identity to load
  if (loading) {
    return null;
  }

  // Check if user is in any of the allowed groups
  const hasAccess = allowedGroups.some(group => userOwnership.includes(group));
  
  if (!hasAccess) {
    return null;
  }

  // Get the entity reference
  const entityRef = stringifyEntityRef(entity);

  return (
    <InfoCard title="Analytics">
      <Box className={classes.content}>
        <BarChartIcon className={classes.icon} />
        <Typography variant="body2" color="textSecondary" paragraph>
          View detailed page view analytics for this user
        </Typography>
        <Link to={`/analytics/user/${encodeURIComponent(entityRef)}`}>
          <Button
            variant="contained"
            color="primary"
            className={classes.button}
            startIcon={<BarChartIcon />}
          >
            View Analytics
          </Button>
        </Link>
      </Box>
    </InfoCard>
  );
};
